import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Box,
  FormInputText,
  IconButton,
  InputLabel,
  Modal,
  Select,
  Svg,
  Text,
} from '@hopskipdrive/react-components';
import { analyticsCanceledAllRides } from '@rideiq/api/services/Analytics';
import { ApiResponse } from '@rideiq/api/utils/responseTransform';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { colorTokens } from '@rideiq/shared/components/variables';
import { useCancelReason } from '@rideiq/shared/hooks/useCancelReason';
import { useToastStore } from '@rideiq/shared/stores/toastStore';

import { cancelRideValidationSchema } from './validationSchema';

export const CancelAllRidesButtonV2 = ({
  riderSlug,
  riderState,
  cancelAllRidesAction,
  hasRidesToCancel,
}: {
  riderSlug: string;
  riderState: string;
  cancelAllRidesAction: (payload) => Promise<ApiResponse<{ message: string }>>;
  hasRidesToCancel: boolean;
}) => {
  const { addToast } = useToastStore();
  const [openModal, setOpenModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const canCancelAllRides = useRef(false);

  if (hasRidesToCancel) {
    canCancelAllRides.current = !['deactivated', 'paused'].includes(riderState);
  }

  const onConfirm = async (formData) => {
    setIsFetching(true);
    const response = await cancelAllRidesAction(formData);

    if (response.type === 'success') {
      analyticsCanceledAllRides(riderSlug);
      setOpenModal(false);
      addToast({ severity: 'success', message: response.data.message });
    } else if (response.type === 'error') {
      response.error.errors.forEach((error: string) => addToast({ severity: 'error', message: error }));
    }
    setIsFetching(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="tertiary"
        size="large"
        disabled={!canCancelAllRides.current}
        onClick={() => setOpenModal(true)}
      >
        Cancel All Rides
      </Button>
      <Modal open={openModal} data-testid="cancel-all-rides">
        <Modal.Title sx={{ padding: '20px 24px 6px' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <PageHeading variant="h1" fontWeight={700}>
              Cancel All Rides
            </PageHeading>
            <IconButton
              aria-label="Close cancel all rides modal"
              onClick={() => setOpenModal(false)}
              sx={{ padding: 0 }}
            >
              <Svg height="20px" icon="close-outlined" />
            </IconButton>
          </Box>
        </Modal.Title>
        <Modal.Content sx={{ padding: '0px 24px 30px' }}>
          <Form handleCancelRide={(formData) => onConfirm(formData)} />
        </Modal.Content>
        <Modal.Actions sx={{ padding: '20px 24px 16px', gap: '10px' }}>
          <Button color="tertiary" onClick={() => setOpenModal(false)}>
            Never mind
          </Button>
          <Button form="cancel-ride-form" color="primary" type="submit" disabled={isFetching}>
            Confirm
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

interface CancelAllRidesFormType {
  reasonKey: string;
}

const Form = ({ handleCancelRide }) => {
  const { cancelReasons } = useCancelReason('series');
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CancelAllRidesFormType>({
    defaultValues: {},
    resolver: yupResolver(cancelRideValidationSchema),
  });

  return (
    <form id="cancel-ride-form" onSubmit={handleSubmit((formData) => handleCancelRide(formData))}>
      <Text
        variant="subtitle2"
        style={{
          color: colorTokens.text.medium,
          marginBottom: '0.75rem',
          marginTop: 0,
          fontSize: '14px',
          fontWeight: 400,
          width: '26rem',
          letterSpacing: '0.1px',
        }}
      >
        This action will cancel all of the rides in which this person is the only rider. Any shared rides won’t be
        cancelled but this person will be removed. This action cannot be undone.
      </Text>
      <Box display="flex" flexDirection="column" gap="3px">
        <InputLabel htmlFor="reasonKey" fieldtype="required" style={{ fontWeight: 300 }}>
          Reason
        </InputLabel>
        <Controller
          control={control}
          name="reasonKey"
          render={({ field: { onChange, value } }) => (
            <Select
              options={cancelReasons}
              value={value}
              setValue={onChange}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <FormInputText
                  {...params}
                  variant="outlined"
                  placeholder="Select a reason"
                  fullWidth
                  error={!!errors.reasonKey}
                  helperText={errors.reasonKey?.message}
                />
              )}
            />
          )}
        />
      </Box>
    </form>
  );
};
